import {
    AfterContentChecked,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    ViewChild,
} from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { AdminAttributes, ReviewStatus, ToggleChangeEvent, ToggleEvent } from "@models/admin.model";
import { Action, DataGridConfig, TableColumn, TableDataResponse } from "@models/data-grid.model";
import { debounceTime, finalize, Observable, Subject, Subscription } from "rxjs";
import { GenericDialogComponent } from "../generic-dialog/generic-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { StructureInstance, StructureStatus } from "@models/structure.model";
import { Router } from "@angular/router";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { TranslateService } from "@ngx-translate/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { SupportMessage } from "@models/support-chat.model";
import { SupportSessionAttributes, SupportSessionStatus } from "@models/supportSession.model";
import { DialogService } from "../../services/dialog/dialog.service";

@Component({
    selector: "app-data-grid",
    templateUrl: "./data-grid.component.html",
    styleUrls: ["./data-grid.component.scss"],
    standalone: false,
})
export class DataGridComponent implements AfterContentChecked, OnChanges {
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    @Input() tableColumns!: TableColumn[];
    @Input() actions: Action[] = [];
    @Input() config!: DataGridConfig;
    @Input() selectedStatus!: string;
    @Input() refreshFlag: boolean = false;
    @Input() showInput: boolean = false;

    @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
    @Output() toggleChange: EventEmitter<ToggleChangeEvent> = new EventEmitter<ToggleChangeEvent>();
    @Output() approveRejectData: EventEmitter<any> = new EventEmitter<any>();
    @Output() columnSearch = new EventEmitter<{ key: string; value: string }[]>();

    @Output() selectionChange = new EventEmitter<{
        selectedIds: Set<number>;
        allSelected: boolean;
        someSelected: boolean;
    }>();
    @Output() Data = new EventEmitter<any>();

    public dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    public dataLoading: boolean = false;
    public dataTableSize = 0;
    public showPaginator = false;
    public subscription!: Subscription;
    public imageObject: Array<object> = [];
    public reviewStatus = ReviewStatus;
    private originalColumns: TableColumn[] = [];
    private columnsInitialized = false;
    public selectedIds: Set<number> = new Set();
    public currentPageIds: Set<number> = new Set();
    public filterData: { key: string; value: string }[] = [];
    private searchSubject = new Subject<void>();
    public SupportSessionStatuses = SupportSessionStatus;

    constructor(
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private router: Router,
        private translate: TranslateService,
        private dialogService: DialogService,
    ) {
        this.searchSubject
            .pipe(debounceTime(2000)) // wait 2 seconds
            .subscribe(() => {
                this.columnSearch.emit(this.filterData);
            });
    }

    ngAfterContentChecked(): void {
        this.cdr.detectChanges();
    }

    ngOnChanges(): void {
        this.selectedStatus = this.selectedStatus;
        if (this.tableColumns && !this.columnsInitialized) {
            this.originalColumns = [...this.tableColumns];
            this.columnsInitialized = true;
        }

        if (this.refreshFlag) {
            this.filterData = [];
        }
    }

    public loadData(tableData: Observable<TableDataResponse>): void {
        if (tableData) {
            this.dataLoading = true;
            setTimeout(() => {
                if (this.paginator) {
                    this.paginator.pageIndex = this.config!.pageIndex;
                    this.paginator.length = this.dataTableSize;
                }
            });
            this.cdr.detectChanges();
            this.dataSource.data = [];

            if (this.subscription) {
                // in case there was one API call already, let's cancel it (to alleviate API requests)
                this.subscription.unsubscribe();
            }

            this.subscription = tableData
                .pipe(
                    finalize(() => {
                        this.dataLoading = false;
                    }),
                )
                .subscribe({
                    next: (data: TableDataResponse) => {
                        this.dataSource.data = data.rows;
                        this.currentPageIds = new Set(data.rows.map((row) => row.id!));
                        this.Data.emit(this.dataSource.data);
                        this.dataTableSize = data.count;

                        if (this.config!.pageSizeOptions) {
                            this.showPaginator = this.dataTableSize > this.config!.pageSizeOptions[0];
                        }

                        setTimeout(() => {
                            if (this.paginator) {
                                this.paginator.pageIndex = this.config.pageIndex;
                                this.paginator.length = data.count;
                            }
                        });

                        this.updateColumnHeaders();
                        this.emitSelectionState();
                    },
                    error: (error) => {
                        throw error;
                    },
                });
        } else {
            this.dataLoading = false;
            this.dataSource.data = [];
            this.showPaginator = false;
        }
    }

    private updateColumnHeaders(): void {
        // Clone the original columns
        this.tableColumns = [...this.originalColumns];

        // Update the 'adminUserId' column header based on status
        const adminColumn = this.tableColumns.find((col) => col.key === "admin.name");
        if (adminColumn) {
            if (this.selectedStatus === ReviewStatus.Pending || this.selectedStatus === StructureStatus.Published) {
                this.tableColumns = this.tableColumns.filter((col) => col.key !== "admin.name");
            } else {
                adminColumn.value =
                    this.selectedStatus === ReviewStatus.Approved || this.selectedStatus === StructureStatus.Approved
                        ? "Approved By"
                        : this.selectedStatus === ReviewStatus.Rejected ||
                          this.selectedStatus === StructureStatus.Rejected
                        ? "Rejected By"
                        : "Actioned By";
            }
        }
    }

    public getColumnProps(prop: keyof TableColumn): string[] {
        return (
            this.tableColumns
                ?.filter((col) => col.key !== "rejectComment" || this.selectedStatus === ReviewStatus.Rejected)
                .map((col) => col[prop])
                .filter((key): key is string => !!key) || []
        );
    }

    public onPageChange(event: PageEvent): void {
        this.pageChange.emit(event);
    }

    public onActionClick(action: Action, id: number) {
        if (action.callback) {
            action.callback(id);
        }
    }

    public onToggleChange(event: ToggleEvent, row: AdminAttributes): void {
        this.toggleChange.emit({ event, row });
    }

    public isOverflow(el: HTMLElement): boolean {
        var curOverflow = el.style.overflow;
        if (!curOverflow || curOverflow === "visible") {
            el.style.overflow = "hidden";
        }
        var isOverflowing = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
        el.style.overflow = curOverflow;
        return isOverflowing;
    }

    public getValue(item: any, key: string): any {
        return key.split(".").reduce((acc, part) => acc && acc[part], item);
    }

    public getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public onStatusButtonClick(item: any) {
        const dialogData = {
            componentName: DialogInvokingComponents.ProductReview,
        };

        this.dialogService
            .openDialog(dialogData, "100%", { disableClose: false }, "500px")
            .afterClosed()
            .subscribe((savedData: any) => {
                if (savedData) {
                    savedData.id = item.id;
                    savedData.productId = item.productId;
                    this.approveRejectData.emit(savedData);
                }
            });
    }

    public onClickImage(imagesUrl: string[]): void {
        this.imageObject = imagesUrl.map((url) => ({ image: url, thumbImage: url }));
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: { imageSlider: this.imageObject, componentName: DialogInvokingComponents.ImageSlider },
        });
    }

    public shouldDisplayUpdatedAt(row: any): boolean {
        return row.createdAt !== row.updatedAt;
    }

    public handleCheckboxChange(rowId: number, event: MatCheckboxChange): void {
        this.updateSelection(rowId, event.checked);
        this.emitSelectionState();
    }

    private updateSelection(id: number, isSelected: boolean): void {
        if (isSelected) {
            this.selectedIds.add(id);
        } else {
            this.selectedIds.delete(id);
        }
    }

    public selectAllRows(isChecked: boolean): void {
        this.currentPageIds.forEach((id) => this.updateSelection(id, isChecked));
        this.emitSelectionState();
    }

    private emitSelectionState(): void {
        const selectedOnPage = Array.from(this.selectedIds).filter((id) => this.currentPageIds.has(id)).length;

        const allSelected = selectedOnPage === this.currentPageIds.size && this.currentPageIds.size > 0;
        const someSelected = selectedOnPage > 0 && selectedOnPage < this.currentPageIds.size;

        this.selectionChange.emit({
            selectedIds: this.selectedIds,
            allSelected,
            someSelected,
        });
    }

    public isSelected(rowId: number): boolean {
        return this.selectedIds.has(rowId);
    }

    public viewChat(row: SupportSessionAttributes): void {
        const dialogData = {
            componentName: DialogInvokingComponents.ChatDialog,
            publicId: row.publicId,
            adminId: row.adminId,
            sessionStatus: row.status,
        };

        this.dialogService.openDialog(dialogData, "100%", { disableClose: false }, "600px");
    }

    public getVisibleActions(row: any): Action[] {
        return this.actions.filter((action) => !action.visible || action.visible(row));
    }

    public applyColumnFilter(event: Event, key: string): void {
        const value = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.filterData = this.filterData.filter((f) => f.key !== key);
        this.filterData.push({ key, value });
        this.searchSubject.next();
    }

    public trackByKey(index: number, col: any): string {
        return col.key;
    }

    getFilterValue(key: string): string {
        return this.filterData.find((f) => f.key === key)?.value || "";
    }
}
